import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { AppLink } from '../AppLink';
import styles from './Pagination.module.scss';

export type PaginationAllowedChildren = React.ReactElement<
  | React.ComponentProps<typeof PaginationLink>
  | React.ComponentProps<typeof PaginationNavLink>
  | React.ComponentProps<typeof PaginationEllipsis>
>;

interface PaginationProps {
  children:
    | PaginationAllowedChildren
    | PaginationAllowedChildren[]
    | (PaginationAllowedChildren | PaginationAllowedChildren[])[];
  ariaLabel: string;
  center?: boolean;
  testId: string;
}

export const Pagination: React.FC<PaginationProps> = ({ children, ariaLabel, center, testId }) => (
  <nav
    aria-label={ariaLabel}
    className={clsx(styles.nav, { [styles.navCenter]: center })}
    data-testid={testId}
  >
    <ul className={styles.list}>{children}</ul>
  </nav>
);

interface PaginationLinkProps {
  children: React.ReactNode;
  title: string;
  href: string;
  isActive?: boolean;
  testId: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Pass-through to next/link. Set false when the consumer handles scroll itself. */
  scroll?: boolean;
}

export const PaginationLink: React.FC<PaginationLinkProps> = ({
  children,
  title,
  href,
  isActive,
  testId,
  onClick,
  scroll,
}) => (
  <li className={styles.item}>
    <AppLink
      href={href}
      className={clsx(styles.link, {
        [styles.linkActive]: isActive,
      })}
      title={title}
      aria-current={isActive ? 'page' : undefined}
      data-testid={testId}
      onClick={onClick}
      prefetch="viewport"
      scroll={scroll}
    >
      {children}
    </AppLink>
  </li>
);

interface PaginationNavLinkProps {
  variant: 'prev' | 'next';
  href?: string;
  testId: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Pass-through to next/link. Set false when the consumer handles scroll itself. */
  scroll?: boolean;
}

export const PaginationNavLink: React.FC<PaginationNavLinkProps> = ({
  variant,
  href,
  testId,
  onClick,
  scroll,
}) => {
  const t = useTranslations('other.pagination');
  const title = t(`${variant}.title`);
  const icon =
    variant === 'prev' ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />;

  if (!href) {
    return (
      <li className={styles.item} aria-hidden="true">
        <span
          className={clsx(styles.linkNav, { [styles.linkDisabled]: true })}
          aria-disabled="true"
        >
          {icon}
        </span>
      </li>
    );
  }

  return (
    <li className={styles.item}>
      <AppLink
        href={href}
        className={styles.linkNav}
        data-testid={testId}
        onClick={onClick}
        prefetch="viewport"
        scroll={scroll}
      >
        <span className="sr-only">{title}</span>
        {icon}
      </AppLink>
    </li>
  );
};

interface PaginationEllipsisProps {
  testId?: string;
}

export const PaginationEllipsis: React.FC<PaginationEllipsisProps> = ({ testId }) => (
  <li className={styles.item} aria-hidden="true">
    <span className={clsx(styles.link, styles.linkEllipsis)} data-testid={testId}>
      …
    </span>
  </li>
);
